/**
 * useCompletionData — Derive completion screen display data and persist the session.
 *
 * UI-INV-003: Hooks are the view-model layer — no computation in JSX.
 * UI-INV-007: Date formatting lives here, not in RetreatBridge JSX.
 * M2-UX-P1-004: Saves the session record exactly once, when `shouldSave` first
 * becomes true (i.e. CompletionScreen is being shown). Calling this hook before
 * a real completion does not write to history.
 * INV-002: Engine is single source of truth — elapsedSeconds comes from snapshot,
 * not from UI-computed timing.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { EditableRetreatSchedule } from "@retreat/core";
import { totalSeconds } from "../utils/scheduleUtils";
import { displayScheduleName, formatDuration } from "../utils/scheduleUtils";

export type CompletionData = {
  date: string;
  completedAt: number;
  sessionSaved: boolean;
  retreatName: string;
  durationLabel: string;
  dateRangeLabel: string;
};

export function useCompletionData(
  schedule: EditableRetreatSchedule | null,
  elapsedSeconds: number = 0,
  shouldSave: boolean = true,
): CompletionData {
  const savedRef = useRef(false);
  const completedAtRef = useRef<number | null>(null);

  // Capture completedAt only when save is requested — not on mount — so the
  // displayed date matches the actual completion time, not the page load time.
  if (shouldSave && completedAtRef.current === null) {
    completedAtRef.current = Date.now();
  }
  const completedAt = completedAtRef.current ?? 0;

  const date = useMemo(
    () =>
      completedAt
        ? new Date(completedAt).toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
          })
        : "",
    [completedAt],
  );

  const [sessionSaved, setSessionSaved] = useState(false);

  useEffect(() => {
    const saveAlreadyDone = savedRef.current || !shouldSave || !completedAt;
    if (saveAlreadyDone) return;
    savedRef.current = true;
    setSessionSaved(true);
  }, [shouldSave, completedAt]);

  const durationSeconds = elapsedSeconds > 0 ? elapsedSeconds : (schedule ? totalSeconds(schedule) : 0);
  const retreatName = schedule ? displayScheduleName(schedule) : "";
  const durationLabel = schedule ? formatDuration(durationSeconds) : "";
  const dateRangeLabel = useMemo(() => {
    if (!completedAt) return "";
    const startMs = completedAt - durationSeconds * 1000;
    const datePart = new Date(startMs).toLocaleDateString([], { month: "long", day: "numeric" });
    const startTime = new Date(startMs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const endTime = new Date(completedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return `${datePart}, ${startTime} → ${endTime}`;
  }, [completedAt, durationSeconds]);

  return { date, completedAt, sessionSaved, retreatName, durationLabel, dateRangeLabel };
}
